import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, X, Send, ShieldCheck, ArrowUpRight } from "lucide-react";

/**
 * VerificationCallButton — admin-only inline button that pops a compact
 * "Schedule Verification Call" dialog. Fixed provider = "meet" (Google Meet
 * via the Calendar API). Uses the target user's email/business_name as the
 * guest and pre-fills a helpful topic + agenda.
 *
 * Renders NOTHING for non-admin users so it can be safely embedded on any
 * row that lists partners.
 *
 * Props:
 *  - api: axios client
 *  - currentUser: from useAuth() — used to gate visibility
 *  - target: { id, email, business_name } — the partner being scheduled
 *  - variant: "primary" | "ghost" — visual style
 *  - label: optional custom button text
 */
export function VerificationCallButton({ api, currentUser, target, variant = "primary", label }) {
  const [open, setOpen] = useState(false);
  if (!currentUser || currentUser.role !== "admin") return null;
  if (!target?.id) return null;

  const cls = variant === "ghost"
    ? "rounded-full border border-white/15 hover:border-[#4285F4] text-[#C9C9C2] hover:text-[#4285F4] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5"
    : "rounded-full bg-[#4285F4] text-white px-4 py-2 text-[10px] uppercase tracking-[0.24em] font-grotesk hover:brightness-110 inline-flex items-center gap-2";

  return (
    <>
      <button data-testid={`verification-call-btn-${target.id}`}
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              className={cls}>
        <Video size={12}/> {label || "Schedule Verification Call"}
      </button>
      {open && (
        <VerificationCallDialog api={api} target={target}
                                 onClose={() => setOpen(false)}
                                 onCreated={() => { setOpen(false); }} />
      )}
    </>
  );
}


function VerificationCallDialog({ api, target, onClose, onCreated }) {
  const [gStatus, setGStatus] = useState({ connected: false, email: "", can_connect: false });
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [form, setForm] = useState({
    title: `KYC verification call · ${target.business_name || target.email}`,
    agenda: "Confirm business identity, GST/PAN, coverage states and onboarding intent.",
    date: "", time: "",
    duration_minutes: 30,
  });

  useEffect(() => {
    api.get("/google/oauth/status")
      .then((r) => setGStatus(r.data))
      .catch(() => setGStatus({ connected: false, email: "", can_connect: false }));
  }, [api]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/google/oauth/start");
      // Redirect the current tab — the callback will bounce back to /admin?meet_connect=ok
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google OAuth");
      setConnecting(false);
    }
  };

  const submit = async () => {
    if (!gStatus.connected) { toast.error("Please connect Google Calendar first"); return; }
    if (!form.date || !form.time) { toast.error("Set a date and time"); return; }
    const start_at = new Date(`${form.date}T${form.time}`).toISOString();
    if (new Date(start_at) < new Date(Date.now() - 60_000)) { toast.error("Pick a future date & time"); return; }
    setBusy(true);
    try {
      const r = await api.post("/meetings", {
        guest_id: target.id,
        title: form.title.trim(),
        agenda: form.agenda.trim(),
        start_at,
        duration_minutes: Number(form.duration_minutes),
        provider: "meet",
        kind: "verification",
      });
      toast.success("Verification call scheduled · Google Meet link created ✔");
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create Meet");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90]" data-testid="verification-call-dialog"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[540px] bg-[#070707] border border-[#4285F4]/30 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-br from-[#4285F4]/[0.06] to-transparent">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#4285F4] font-grotesk flex items-center gap-2">
              <ShieldCheck size={12}/> Schedule Verification Call · Google Meet
            </div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">{target.business_name || target.email}</div>
          </div>
          <button onClick={onClose} data-testid="verification-call-close" className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={16}/></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Connection status */}
          {gStatus.connected ? (
            <div className="rounded-sm border border-[#2FB999]/30 bg-[#2FB999]/[0.05] px-3 py-2.5 flex items-center justify-between" data-testid="gcal-status-connected">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#2FB999] font-grotesk">Google connected</div>
                <div className="text-[11px] text-[#C9C9C2] font-body mt-0.5">{gStatus.email || "shared admin account"}</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk">Meet</span>
            </div>
          ) : (
            <div className="rounded-sm border border-[#D8B23E]/30 bg-[#D8B23E]/[0.05] px-4 py-4" data-testid="gcal-status-disconnected">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D8B23E] font-grotesk">Google not connected</div>
              <div className="text-[12px] text-[#C9C9C2] font-body mt-1 leading-relaxed">
                Connect your Google account once — from then on every admin can create Meet links from this button.
              </div>
              <button data-testid="gcal-connect-btn" onClick={connect} disabled={connecting}
                      className="mt-3 rounded-full bg-[#4285F4] text-white px-4 py-2 text-[10px] uppercase tracking-[0.24em] font-grotesk hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-2">
                {connecting ? <Loader2 size={12} className="animate-spin"/> : <ArrowUpRight size={12}/>}
                Connect Google
              </button>
            </div>
          )}

          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Topic *</label>
            <input data-testid="vcall-title" value={form.title}
                   onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#4285F4]" />
          </div>
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Agenda</label>
            <textarea data-testid="vcall-agenda" rows={3} value={form.agenda}
                      onChange={(e) => setForm((f) => ({ ...f, agenda: e.target.value }))}
                      className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#4285F4] resize-none" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Date *</label>
              <input data-testid="vcall-date" type="date" value={form.date}
                     onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#4285F4]" />
            </div>
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Time *</label>
              <input data-testid="vcall-time" type="time" value={form.time}
                     onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#4285F4]" />
            </div>
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Duration</label>
              <input data-testid="vcall-duration" type="number" min="5" max="480" value={form.duration_minutes}
                     onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#4285F4]" />
            </div>
          </div>

          <button data-testid="vcall-submit" onClick={submit} disabled={busy || !gStatus.connected}
                  className="w-full rounded-full bg-[#4285F4] text-white px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            Create Meet & Send Invite
          </button>
          <p className="text-[10px] text-[#8A8A82] text-center leading-relaxed">
            Google auto-sends a native calendar invite with the <span className="text-[#4285F4]">Meet</span> link to both attendees.
          </p>
        </div>
      </div>
    </div>
  );
}


/**
 * GoogleMeetConnectionCard — panel used on the Admin ▸ Meetings tab so the
 * admin can see connection status and connect/disconnect Google outside of
 * the per-partner scheduling flow.
 */
export function GoogleMeetConnectionCard({ api }) {
  const [st, setSt] = useState({ connected: false, email: "", connected_at: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/google/oauth/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // If we've just come back from the Google consent flow, refresh once
    // more after a short delay so the DB write has settled.
    const params = new URLSearchParams(window.location.search);
    if (params.get("meet_connect") === "ok") {
      setTimeout(load, 500);
      toast.success(`Google Meet connected${params.get("as") ? " · " + params.get("as") : ""}`);
    } else if (params.get("meet_connect") === "error") {
      toast.error("Google Meet connection failed: " + (params.get("msg") || "unknown"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/google/oauth/start");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?\n\nExisting Meet meetings will keep working (the events already exist on Google), but you won't be able to schedule new ones until you reconnect.")) return;
    try {
      await api.post("/google/oauth/disconnect");
      toast.success("Google disconnected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="border rounded-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
         data-testid="gcal-connection-card"
         style={{ borderColor: st.connected ? "rgba(47,185,153,0.35)" : "rgba(66,133,244,0.35)" }}>
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <div className="w-12 h-12 rounded-full grid place-items-center shrink-0 text-white"
             style={{ background: st.connected ? "#2FB999" : "#4285F4" }}>
          <Video size={20}/>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] font-grotesk"
               style={{ color: st.connected ? "#2FB999" : "#4285F4" }}>
            {st.connected ? "Google Meet · Connected" : "Google Meet · Not connected"}
          </div>
          <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">
            {st.connected ? (st.email || "Shared admin account") : "Add Google Meet links to Vinci meetings"}
          </div>
          <p className="font-body text-xs text-[#8A8A82] mt-1 max-w-lg leading-relaxed">
            {st.connected
              ? "Any admin can now click 'Schedule Verification Call' on a partner profile to create a real Meet link + a native Google calendar invite."
              : "Connect once and every admin can create Google Meet links from any partner's profile. Jitsi stays as the default provider for everyone else."}
          </p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {st.connected ? (
          <button data-testid="gcal-disconnect-btn" onClick={disconnect} disabled={busy}
                  className="rounded-full border border-white/15 hover:border-[#C0392B] text-[#C9C9C2] hover:text-[#C0392B] px-4 py-2 text-[10px] uppercase tracking-[0.24em] font-grotesk">
            Disconnect
          </button>
        ) : (
          <button data-testid="gcal-connect-btn-card" onClick={connect} disabled={busy}
                  className="rounded-full bg-[#4285F4] text-white px-5 py-2.5 text-[10px] uppercase tracking-[0.24em] font-grotesk hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-2">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <ArrowUpRight size={12}/>}
            Connect Google
          </button>
        )}
      </div>
    </div>
  );
}
